import { useEffect, useMemo, useState, useCallback } from "react";
import * as yup from "yup";
import axios from "axios";
import Header from "../../components/Header";
import SimpleForm from "../../components/SimpleForm";
import { Snackbar, Alert, CircularProgress, Box } from "@mui/material";

/* ---------- axios ---------- */
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  timeout: 15000,
});

/* ---------- helpers ---------- */
const buildInitials = (fields) =>
  fields.reduce((acc, f) => {
    const def =
      f.default ??
      (f.type === "checkbox"
        ? false
        : f.type === "number"
        ? ""
        : f.type === "date"
        ? ""
        : "");
    acc[f.name] = def;
    return acc;
  }, {});

const buildSchema = (fields) => {
  const shape = {};
  fields.forEach((f) => {
    let rule;
    switch (f.type) {
      case "number":
        rule = yup.number().typeError("must be a number");
        break;
      case "checkbox":
        rule = yup.boolean();
        break;
      case "date":
        rule = yup.date().typeError("invalid date");
        break;
      case "select":
        rule = yup.string();
        break;
      default:
        rule = yup.string();
    }
    if (f.required) rule = rule.required("required");
    shape[f.name] = rule;
  });
  return yup.object().shape(shape);
};

async function postJSON(path, data) {
  try {
    const res = await api.post(path, data);
    return { data: res.data, error: null };
  } catch (err) {
    // Extract error message from response - prioritize the message field
    const serverError = err?.response?.data?.message || 
                       err?.response?.data?.errors ||
                       err?.response?.data?.error ||
                       err.message;
    
    // Return error as a string or object
    return { 
      data: null, 
      error: typeof serverError === 'object' ? JSON.stringify(serverError) : serverError 
    };
  }
}

/* ---------- page ---------- */
export default function RegisterFsMachine() {
  const [toast, setToast] = useState({ open: false, msg: "", sev: "success" });
  const [loadingOpts, setLoadingOpts] = useState(true);
  const [userOptions, setUserOptions] = useState([]);
  const [lastRefreshed, setLastRefreshed] = useState(Date.now());

  const lsUser = (() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "{}");
    } catch {
      return {};
    }
  })();

  const roleRaw = String(
    lsUser?.role ||
      lsUser?.userRole ||
      lsUser?.roleName ||
      lsUser?.type ||
      ""
  )
    .toLowerCase()
    .trim();

  const isSalesMan = /(sales\s*-?\s*man|salesman|sales\s*person|salesperson|^sales$)/i.test(
    roleRaw
  );
  const currentUserId = String(
    lsUser?.id ?? lsUser?.userId ?? lsUser?._id ?? ""
  );

  /* ---------- loadOptions ---------- */
  const loadOptions = useCallback(async () => {
    setLoadingOpts(true);
    try {
      const res = await api.get("/api/user", {
        params: { t: Date.now() },
        headers: { "Cache-Control": "no-cache" },
      });

      const arr = (x) => (Array.isArray(x) ? x : []);
      const usersRaw = arr(res?.data);

      const uOpts = usersRaw
        .filter((u) => {
          const role = String(u?.Role?.name || u?.role || "").toLowerCase();
          return role.includes("sales");
        })
        .sort((a, b) =>
          String(a?.fullName || a?.userName || "").localeCompare(
            String(b?.fullName || b?.userName || "")
          )
        )
        .map((u) => ({
          value: String(u?.id ?? u?.userId ?? u?._id),
          label:
            u?.fullName ||
            u?.name ||
            u?.userName ||
            u?.username ||
            u?.email ||
            `User ${u?.id}`,
        }));

      setUserOptions(uOpts);
      setLastRefreshed(Date.now());
    } catch (e) {
      setToast({
        open: true,
        msg: "Failed to load sales users",
        sev: "error",
      });
    } finally {
      setLoadingOpts(false);
    }
  }, []);

  useEffect(() => {
    loadOptions();
  }, [loadOptions]);

  /* ---------- fields ---------- */
  const FIELDS = useMemo(() => {
    const userField = isSalesMan
      ? null
      : {
          name: "userId",
          label: "Sales User",
          type: "select",
          span: 2,
          required: true,
          options: userOptions,
          key: `user-${lastRefreshed}`,
          props: {
            disabled: loadingOpts || userOptions.length === 0,
            placeholder: loadingOpts ? "Loading..." : "Choose sales user",
          },
        };

    const fields = [
      userField,
      {
        name: "machineNo",
        label: "Machine Number",
        type: "number",
        span: 2,
        required: true,
        props: {
    placeholder: "Enter amount",
    inputProps: { step: 0.01, min: 0 },
    onKeyDown: (e) => {
      if (e.key === "-" || e.key === "e") {
        e.preventDefault();
      }
    }
  }
      },
      {
        name: "fsNo",
        label: "FS Number",
        type: "text",
        span: 2,
        required: true,
       props: {
    placeholder: "Enter amount",
    inputProps: { step: 0.01, min: 0 },
    onKeyDown: (e) => {
      if (e.key === "-" || e.key === "e") {
        e.preventDefault();
      }
    }
  }
      },
    ].filter(Boolean);

    return fields;
  }, [isSalesMan, userOptions, loadingOpts, lastRefreshed]);

  const SCHEMA = useMemo(() => buildSchema(FIELDS), [FIELDS]);
  const INITIALS = useMemo(() => buildInitials(FIELDS), [FIELDS]);

  /* ---------- submit ---------- */
 const handleSubmit = async (values, { setSubmitting, setErrors, resetForm }) => {
  const payload = {
    userId: Number(isSalesMan ? currentUserId : values.userId),
    machineNo: Number(values.machineNo),
    fsNo: String(values.fsNo),
  };

  const { data, error } = await postJSON("/api/machinfs", payload);
  setSubmitting(false);

  if (error) {
    // Show the server error message in toast
    const errorMessage = error || "Failed to register FS Machine";
    setToast({ 
      open: true, 
      msg: errorMessage, 
      sev: "error" 
    });
    
    // Optionally set form errors if needed
    if (typeof error === "object" && !Array.isArray(error)) {
      setErrors(error);
    }
    return;
  }

  const msg = data?.message || "FS Machine registered successfully!";
  setToast({ open: true, msg, sev: "success" });
  resetForm();
  await loadOptions();
};

  const LoadingBar = loadingOpts ? (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
      <CircularProgress size={18} />
      <span style={{ fontSize: 14, opacity: 0.8 }}>Loading sales users…</span>
    </Box>
  ) : null;

  return (
    <>
      <SimpleForm
        title="REGISTER FS MACHINE"
        subtitle="Assign a fiscal machine to a sales user"
        Header={Header}
        fields={FIELDS}
        initialValues={INITIALS}
        validation={SCHEMA}
        onSubmit={handleSubmit}
        submitLabel="Register Machine"
        columns={4}
      />

      {LoadingBar}

      <Snackbar
       anchorOrigin={{ vertical: 'top', horizontal: 'right'}}
      sx={{ mt: 5 }}
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast((s) => ({ ...s, open: false }))}
      >
        <Alert
          onClose={() => setToast((s) => ({ ...s, open: false }))}
          severity={toast.sev}
          variant="filled"
        >
          {toast.msg}
        </Alert>
      </Snackbar>
    </>
  );
}

